import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import PublicLayout from "@/components/layout/PublicLayout";
import AdminLayout from "@/components/layout/AdminLayout";
import AgentLayout from "@/components/layout/AgentLayout";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ProductsHubPage from "@/pages/ProductsHubPage";
import ServicesHubPage from "@/pages/ServicesHubPage";
import DynamicPage from "@/pages/DynamicPage";
import PartnersPage from "@/pages/PartnersPage";
import ContactPage from "@/pages/ContactPage";
import EMICalculatorPage from "@/pages/EMICalculatorPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminMenu from "@/pages/admin/AdminMenu";
import AdminPages from "@/pages/admin/AdminPages";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminContent from "@/pages/admin/AdminContent";
import AdminAgents from "@/pages/admin/AdminAgents";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminPayouts from "@/pages/admin/AdminPayouts";
import AdminTwilio from "@/pages/admin/AdminTwilio";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminDeployment from "@/pages/admin/AdminDeployment";
import GalleryPage from "@/pages/GalleryPage";
import AgentLogin from "@/pages/agent/AgentLogin";
import AgentRegister from "@/pages/agent/AgentRegister";
import AgentDashboard from "@/pages/agent/AgentDashboard";
import AgentLeads from "@/pages/agent/AgentLeads";
import AgentNewLead from "@/pages/agent/AgentNewLead";
import AgentPayouts from "@/pages/agent/AgentPayouts";
import AgentProfile from "@/pages/agent/AgentProfile";
import "@/App.css";

function ProtectedAdmin({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-ink-muted">Checking session…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/products" element={<ProductsHubPage />} />
              <Route path="/services" element={<ServicesHubPage />} />
              <Route path="/partners" element={<PartnersPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/emi-calculator" element={<EMICalculatorPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/p/:slug" element={<DynamicPage />} />
            </Route>

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route element={<ProtectedAdmin><AdminLayout /></ProtectedAdmin>}>
              <Route path="/admin" element={<Navigate to="/admin/leads" replace />} />
              <Route path="/admin/leads" element={<AdminLeads />} />
              <Route path="/admin/messages" element={<AdminMessages />} />
              <Route path="/admin/agents" element={<AdminAgents />} />
              <Route path="/admin/payouts" element={<AdminPayouts />} />
              <Route path="/admin/content" element={<AdminContent />} />
              <Route path="/admin/menu" element={<AdminMenu />} />
              <Route path="/admin/pages" element={<AdminPages />} />
              <Route path="/admin/partners" element={<AdminPartners />} />
              <Route path="/admin/twilio" element={<AdminTwilio />} />
              <Route path="/admin/gallery" element={<AdminGallery />} />
              <Route path="/admin/deployment" element={<AdminDeployment />} />
            </Route>

            <Route path="/agent/login" element={<AgentLogin />} />
            <Route path="/agent/register" element={<AgentRegister />} />
            <Route element={<AgentLayout />}>
              <Route path="/agent" element={<Navigate to="/agent/dashboard" replace />} />
              <Route path="/agent/dashboard" element={<AgentDashboard />} />
              <Route path="/agent/leads" element={<AgentLeads />} />
              <Route path="/agent/new-lead" element={<AgentNewLead />} />
              <Route path="/agent/payouts" element={<AgentPayouts />} />
              <Route path="/agent/profile" element={<AgentProfile />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
